import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, LogOut, Star } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About Us" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function AuthPill({ mobile = false }) {
  const { user, logout, loading, currentMode } = useAuth();
  const navigate = useNavigate();
  if (loading) return null;
  // No guest "Sign in" affordance — the marketing site focuses on the
  // mobile app first. Signed-in users still get their dashboard badge.
  if (!user) return null;
  const goToDashboard = () => navigate(currentMode === "owner" ? "/owner" : "/passport");
  const avatar = (currentMode === "owner" ? user.avatar_owner : user.avatar_camper) || user.avatar_data || user.avatar || null;
  return (
    <div className={`flex items-center gap-3 ${mobile ? "w-full justify-between" : ""}`}>
      <button
        onClick={goToDashboard}
        className="flex items-center gap-2 min-w-0"
        data-testid={mobile ? "mobile-user-badge" : "user-badge"}
      >
        {avatar ? (
          <img src={avatar} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white/30" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-[#1F4A2E]" style={{ background: "#FCD34D" }}>
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
        )}
        <div className="flex flex-col leading-tight min-w-0 text-left">
          <span className="text-white text-sm font-semibold truncate max-w-[160px]">
            {user.display_name || user.name}
          </span>
          {user.is_premium && (
            <span
              className="inline-flex items-center gap-1 self-start mt-0.5 px-2 py-[2px] rounded-full text-[10px] font-extrabold uppercase tracking-[0.08em] truncate max-w-[160px]"
              style={{
                background: "rgba(252,211,77,0.16)",
                color: "#FCD34D",
                border: "1px solid rgba(252,211,77,0.45)",
              }}
              data-testid={mobile ? "mobile-plan-badge" : "plan-badge"}
            >
              <Star className="w-2.5 h-2.5" fill="#FCD34D" stroke="#FCD34D" />
              <span className="truncate">Premium</span>
            </span>
          )}
        </div>
      </button>
      <button
        onClick={logout}
        data-testid={mobile ? "mobile-sign-out" : "sign-out"}
        className="mode-toggle"
        title="Sign out"
      >
        <LogOut className="w-4 h-4" />
        {mobile && <span>Sign out</span>}
      </button>
    </div>
  );
}

export default function Header() {
  const { mode, auto, toggle } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <header data-testid="site-header" className="floating-header">
      <div className="wrap flex items-center justify-between h-[68px] md:h-[76px] px-3 md:px-5">
        {/* Logo */}
        <NavLink to="/" data-testid="header-logo" className="flex items-center gap-3">
          <img
            src="/assets/brand/logo-header.png"
            alt="MyAdventure-Passport"
            width={46}
            height={48}
            className="w-[46px] h-[48px] object-contain drop-shadow"
          />
          <div className="leading-tight">
            <div className="font-extrabold tracking-[0.14em] text-[13px] md:text-sm text-white">
              MYADVENTURE
            </div>
            <div className="text-[9px] md:text-[10px] tracking-[0.28em] font-bold text-[#FCD34D]">
              — PASSPORT —
            </div>
          </div>
        </NavLink>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-pill ${isActive ? "active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="mode-toggle"
            aria-label={`Switch to ${mode === "day" ? "night" : "day"} mode`}
          >
            {mode === "day" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            <span>{mode === "day" ? "Night" : "Day"}</span>
            {auto && (
              <span data-testid="auto-badge" className="auto-badge">AUTO</span>
            )}
          </button>
          <AuthPill />
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#1F4A2E] rounded-b-[22px]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `nav-pill text-left ${isActive ? "active" : ""}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center justify-between pt-3 border-t border-white/10 mt-2">
                <button onClick={toggle} className="mode-toggle" data-testid="mobile-theme-toggle">
                  {mode === "day" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                  <span>{mode === "day" ? "Night" : "Day"}</span>
                  {auto && <span className="auto-badge">AUTO</span>}
                </button>
              </div>
              <AuthPill mobile />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
